import React from 'react';
import dayjs from 'dayjs';
import { FieldError, FieldValues, useController, UseControllerProps } from 'react-hook-form';
import {
  Datepicker as BuildersDatepicker,
  HelpText,
  Label,
  ValidationMessage,
} from '@gjensidige/builders-components';

type DatepickerProps<T extends FieldValues> = {
  labelText: string;
  error: FieldError | undefined;
  helpText?: string;
  formatDayJs: string;
} & UseControllerProps<T> &
  Omit<React.ComponentProps<typeof BuildersDatepicker>, 'value' | 'defaultValue' | 'onChange'>;

const Datepicker = <T extends FieldValues>({
  name,
  control,
  labelText,
  error,
  helpText,
  formatDayJs,
  ...props
}: DatepickerProps<T>) => {
  const { field } = useController({ name, control });
  const [localDate, setLocalDate] = React.useState<Date | undefined>();
  const showValidationMessage = error !== undefined;

  const handleOnChange = (date: Date | undefined) => {
    field.onChange(date ? dayjs(date).format(formatDayJs) : '');
  };

  return (
    <>
      <Label htmlFor={name}>
        {labelText}
        {helpText !== undefined && <HelpText>{helpText}</HelpText>}
      </Label>
      <BuildersDatepicker
        id={name}
        value={localDate}
        onChange={(date) => {
          handleOnChange(date);
          setLocalDate(date);
        }}
        tracking={false}
        {...props}
      />
      <ValidationMessage id={name} hidden={!showValidationMessage} invalid={showValidationMessage}>
        {error?.message}
      </ValidationMessage>
    </>
  );
};

export default Datepicker;
